import React, { useEffect, useState } from 'react';
import { TopNav } from '../components/TopNav';
import { useNavigate } from 'react-router-dom';
import { KpiCard } from '../components/KpiCard';
import { listArtists } from '@/features/artists/services/artists.api';
import { listActivities } from '@/services/activities';
import { listPartnerships } from '@/services/partnerships';
import { listKanban } from '@/services/kanban';

interface DashboardProps {
  theme: string;
  toggleTheme: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ theme, toggleTheme }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ artists: 0, activities: 0, partnerships: 0, tasks: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [a, act, p, k] = await Promise.allSettled([
          listArtists(0, 50),
          listActivities(0, 50),
          listPartnerships(),
          listKanban(),
        ]);
        setCounts({
          artists: a.status === 'fulfilled' ? a.value.items.length : 0,
          activities: act.status === 'fulfilled' ? act.value.items.length : 0,
          partnerships: p.status === 'fulfilled' ? p.value.length : 0,
          tasks: k.status === 'fulfilled' ? k.value.length : 0,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-body flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4" />
          <p className="text-zinc-600 dark:text-zinc-400">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <TopNav theme={theme} toggleTheme={toggleTheme} />
      <div className="animate-fade-in space-y-6">
              <header>
                <h1 className="text-2xl font-extrabold">Dashboard</h1>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">Visão geral rápida do ambiente.</p>
              </header>

              {/* KPIs */}
              <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                <KpiCard
                  kpi={{ title: 'Artistas', iconClass: 'ph ph-users', value: String(counts.artists), subValue: 'cadastrados' }}
                  delay="0ms"
                  onClick={() => navigate('/artists')}
                />
                <KpiCard
                  kpi={{ title: 'Atividades', iconClass: 'ph ph-activity', value: String(counts.activities), subValue: 'últimos registros' }}
                  delay="60ms"
                  onClick={() => navigate('/analytics')}
                />
                <KpiCard
                  kpi={{ title: 'Workflow', iconClass: 'ph ph-kanban', value: String(counts.tasks), subValue: 'tarefas pendentes' }}
                  delay="120ms"
                  onClick={() => navigate('/workflow')}
                />
              </section>

              {/* Navegação principal */}
              <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('/artists')}
                  onKeyDown={(e) => e.key === 'Enter' && navigate('/artists')}
                  className="cursor-pointer group bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-900 rounded-lg p-6 border border-zinc-200 dark:border-zinc-700 hover:border-spotify-green/30 transition-all duration-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1DB954]"
                >
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-spotify-green/10 rounded-lg flex items-center justify-center mr-3">
                      <i className="ph ph-users text-spotify-green text-lg" />
                    </div>
                    <h3 className="font-semibold text-zinc-900 dark:text-white">Artistas</h3>
                  </div>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">Gerenciamento completo do roster</p>
                  <div className="text-xs text-zinc-500 dark:text-zinc-500">{counts.artists} artistas</div>
                </div>

                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('/analytics')}
                  onKeyDown={(e) => e.key === 'Enter' && navigate('/analytics')}
                  className="cursor-pointer group bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-900 rounded-lg p-6 border border-zinc-200 dark:border-zinc-700 hover:border-spotify-green/30 transition-all duration-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1DB954]"
                >
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mr-3">
                      <i className="ph ph-chart-line text-blue-500 text-lg" />
                    </div>
                    <h3 className="font-semibold text-zinc-900 dark:text-white">Análises</h3>
                  </div>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">KPIs e métricas detalhadas</p>
                  <div className="text-xs text-zinc-500 dark:text-zinc-500">{counts.activities} atividades</div>
                </div>

                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('/workflow')}
                  onKeyDown={(e) => e.key === 'Enter' && navigate('/workflow')}
                  className="cursor-pointer group bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-900 rounded-lg p-6 border border-zinc-200 dark:border-zinc-700 hover:border-spotify-green/30 transition-all duration-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1DB954]"
                >
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mr-3">
                      <i className="ph ph-kanban text-purple-500 text-lg" />
                    </div>
                    <h3 className="font-semibold text-zinc-900 dark:text-white">Workflow</h3>
                  </div>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">Planejamento semanal</p>
                  <div className="text-xs text-zinc-500 dark:text-zinc-500">{counts.tasks} tarefas</div>
                </div>
              </section>

              {/* Ações rápidas */}
              <section>
                <h2 className="text-base font-semibold text-zinc-900 dark:text-white mb-3">Acesso Rápido</h2>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => navigate('/artists?new=1')} className="px-4 py-2 bg-spotify-green text-white rounded-lg hover:bg-spotify-green/90 transition-colors text-sm font-medium">Novo Artista</button>
                  <button onClick={() => navigate('/analytics')} className="px-4 py-2 bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 rounded-lg hover:bg-zinc-300 dark:hover:bg-zinc-600 transition-colors text-sm font-medium">Relatório Semanal</button>
                  <button onClick={() => navigate('/settings')} className="px-4 py-2 bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 rounded-lg hover:bg-zinc-300 dark:hover:bg-zinc-600 transition-colors text-sm font-medium">Configurações</button>
                </div>
              </section>
      </div>
    </>
  );
};

export default Dashboard;

